<template>
  <div :class="{
      'order-details': true,
      'my-xl md:my-2xl': !authenticated
    }"
  >
    <div class="flex items-center justify-between flex-wrap gap-sm mb-xl md:mb-2xl">
      <h2>
        {{ isCancelled ? 'Cancelled ' : '' | t }}{{ 'Order' | t }} #{{ order?.orderNumber | formatOrderNumber }}
      </h2>
      <SloLink
        v-if="authenticated"
        :to="$route.query.back || {name: 'marketplace-account-orders'}"
      >
        <SloIcon name="ArrowLeft" class="mr-xs" />
        <span class="text-base fw-strong">{{ 'Back' | t }}</span>
      </SloLink>
      <SloLink v-else :to="{name: 'marketplace-auth-login'}">
        <SloIcon name="ArrowLeft" class="mr-xs" />
        <span class="text-base fw-strong">{{ 'Back' | t }}</span>
      </SloLink>
    </div>
    <transition name="fade-up">
      <SloModal v-if="cancelOrderModal" :max-width="500" @dismiss="handleCancelCancelOrder">
        <template v-slot:title>{{ 'Cancel this entire order?' | t }}</template>
        <p>{{ 'Please let us know why you would like to cancel this order.' | t }}</p>
        <SloField>
          <SloLabel for="cancelReason">{{ 'Cancellation reason' | t }}</SloLabel>
          <SloSelect v-model="cancelReason" :placeholder="'Please select' | t" id="cancelReason">
            <option
              v-for="(cancelStatus, index) in cancelStatuses"
              :key="`cancel-status-${index}`"
              v-html="cancelStatus.Text"
              :value="cancelStatus.Key"
            />
          </SloSelect>
        </SloField>
        <template v-slot:footer>
          <SloButton
            @click="handleCancelOrder"
            :disabled="cancelOrderLoading || !cancelReason"
            type="submit"
            fill="outline"
            class="flex-1"
          >
            <SloLoader :loading="cancelOrderLoading" size="md">
              {{ 'Yes, cancel my order' | t }}
            </SloLoader>
          </SloButton>
          <SloButton @click="handleCancelCancelOrder" :disabled="cancelOrderLoading" class="flex-1">
            {{ 'No, keep my order' | t }}
          </SloButton>
        </template>
      </SloModal>
    </transition>
    <MarketplaceTable>
      <template v-slot:header>
        <MarketplaceTableHeader>
          <template v-slot:titles>
            <div class="flex items-start justify-between md:flex-col md:justify-start">
              <h4 class="text-base fw-strong">{{ 'Order Number' | t }}</h4>
              <span class="text-base c-gray-700">{{ order?.orderNumber | formatOrderNumber }}</span>
            </div>
            <div class="flex items-start justify-between md:flex-col md:justify-start">
              <h4 class="text-base fw-strong">{{ 'Date Placed' | t }}</h4>
              <span class="text-base c-gray-700">{{ order?.orderDate }}</span>
            </div>
          </template>
          <template v-slot:buttons>
            <SloButton @click="handlePrintOrder" fill="outline">
              {{ 'Print Order' | t }}
            </SloButton>
            <ReorderOrderButton v-if="authenticated" :order="order" />
            <SloButton
              v-if="canReturn && authenticated"
              :to="{
                name: 'marketplace-account-order-return',
                params: {code: order?.orderNumber},
                query: {back: $route.query.back}
              }"
              fill="outline"
            >
              {{ 'Return Order' | t }}
            </SloButton>
            <SloButton
              v-if="canReturn && !authenticated"
              :to="{
                name: 'marketplace-guest-order-return',
                query: {
                  code: order?.orderNumber,
                  email: order?.createdBy,
                  billZip: order?.paymentInfo?.billingAddress?.postalCode
                }
              }"
              fill="outline"
            >
              {{ 'Return Order' | t }}
            </SloButton>
            <SloButton
              v-if="canCancel"
              fill="outline"
              @click="openCancelOrderModal"
            >
              {{ 'Cancel Order' | t }}
            </SloButton>
          </template>
        </MarketplaceTableHeader>
      </template>
      <template v-slot:order-details-col-1>
        <h4>{{ 'Shipping Address' | t }}</h4>
        <p>{{ order?.shippingAddress?.firstName }}</p>
        <p v-if="order?.shippingAddress?.attention">
          ATTN: {{ order?.shippingAddress?.attention }}
        </p>
        <p v-if="order?.shippingAddress?.companyName">
          {{ order?.shippingAddress?.companyName }}
        </p>
        <p>{{ order?.shippingAddress?.line1 }}</p>
        <p>{{ order?.shippingAddress?.line2 }}</p>
        <p>{{ order?.shippingAddress?.town }} {{ order?.shippingAddress?.region?.isocode }}, {{ order?.shippingAddress?.postalCode }}</p>
        <p>{{ order?.shippingAddress?.country?.isocode }}</p>
      </template>
      <template v-slot:order-details-col-2>
        <h4>{{ 'Billing Address' | t }}</h4>
        <p>{{ order?.paymentInfo?.billingAddress?.firstName }}</p>
        <p v-if="order?.paymentInfo?.billingAddress?.lastName">
          {{ order?.paymentInfo?.billingAddress?.lastName }}
        </p>
        <p>{{ order?.paymentInfo?.billingAddress?.line1 }}</p>
        <p>{{ order?.paymentInfo?.billingAddress?.line2 }}</p>
        <p>{{ order?.paymentInfo?.billingAddress?.town }} {{ order?.paymentInfo?.billingAddress?.region?.isocode }}, {{ order?.paymentInfo?.billingAddress?.postalCode }}</p>
        <p>{{ order?.paymentInfo?.billingAddress?.country?.isocode }}</p>
      </template>
      <template v-slot:order-details-col-3>
        <h4>{{ 'Payment Method' | t }}</h4>
        <p class="flex flex-row items-center gap-sm flex-wrap lg:flex-nowrap">
          <span class="c-gray-900">
            <SloIcon
              :name="order?.paymentInfo?.cardType?.name | getCardTypeIconName"
              size="lg"
            />
          </span>
          <span class="text-capitalize no-wrap">{{ order?.paymentInfo?.cardType?.code }}</span>
          <span class="lg:no-wrap">•••• {{ order?.paymentInfo?.cardNumber | getCardNumberLastFourDigits }}</span>
        </p>
      </template>
      <template v-if="order.customerPONumber" v-slot:order-details-col-4>
        <h4>{{ 'Customer PO Number' | t }}</h4>
        <p class="customer-po-number">{{ order?.customerPONumber }}</p>
      </template>
      <template v-slot:body>
        <div v-for="(entry, entryIndex) in order?.entries" :key="`entry-${entryIndex}`">
          <MarketplaceRow
            v-for="(orderEntry, orderEntryIndex) in entry?.orderEntries"
            :key="`order-entry-${orderEntryIndex}`"
            :title="orderEntry?.product?.number"
            :description="orderEntry?.product?.title"
            :sku="orderEntry?.product?.code"
            :product-type="orderEntry?.product?.type"
            :price="getProductPrice(orderEntry)"
            :uom="orderEntry?.product?.uom"
            :quantity="orderEntry?.quantity"
            :total="orderEntry?.totalPrice?.formattedValue"
            :trackingUrl="orderEntry?.trackingUrl"
            :show-reorder-button="!orderEntry?.product?.subscriptionNumber"
            :subscription-id="orderEntry?.product?.subscriptionNumber"
            :subscription-frequency="orderEntry?.product?.formattedFrequency"
            :subscription-percentage-discount="orderEntry?.product?.subscriptionPercentageDiscount"
          >
            <template v-slot:image>
              <SloProductImage :source="orderEntry?.product?.images | getProductThumbnail" />
            </template>
            <template
              v-if="authenticated && orderEntry?.product?.subscriptionNumber"
              v-slot:subscription-update
            >
              <router-link
                :to="{
                  name: 'marketplace-account-subscription',
                  params: {subscriptionId: orderEntry?.product?.subscriptionNumber}
                }"
                class="text-sm fw-strong mt-sm block"
              >
                {{ 'Edit Subscription' | t }}
              </router-link>
            </template>
            <template v-slot:returns>
              <div
                v-for="(returnRequest, index) in orderEntry?.returnRequests"
                :key="`return-request-${index}`"
                class="flex items-start justify-start gap-sm"
              >
                <span class="c-gray-600">
                  <SloIcon name="ArrowTurnDownRight" />
                </span>
                <div class="marketplace-order--return bg-gray-100 w-100">
                  <div class="flex flex-col items-start gap-sm mb-lg md:flex-row md:gap-md">
                    <p class="text-base fw-strong m-0">{{ 'Returned items' | t }}</p>
                    <p class="m-0">
                      {{ 'Return order number' | t }}:
                      <SloLink :to="getReturnRequestRouterObject(returnRequest)">
                        {{ returnRequest?.rma | formatOrderNumber }}
                      </SloLink>
                    </p>
                  </div>
                  <MarketplaceRow
                    :title="getOrderEntryReturnRequestEntry(orderEntry, returnRequest)?.orderEntry?.product?.number"
                    :description="getOrderEntryReturnRequestEntry(orderEntry, returnRequest)?.orderEntry?.product?.title"
                    :sku="getOrderEntryReturnRequestEntry(orderEntry, returnRequest)?.orderEntry?.product?.code"
                    :product-type="getOrderEntryReturnRequestEntry(orderEntry, returnRequest)?.orderEntry?.product?.type"
                    :price="getOrderEntryReturnRequestEntry(orderEntry, returnRequest)?.orderEntry?.basePrice?.formattedValue"
                    :uom="getOrderEntryReturnRequestEntry(orderEntry, returnRequest)?.orderEntry?.product?.uom"
                    :quantity="getOrderEntryReturnRequestEntry(orderEntry, returnRequest)?.expectedQuantity"
                    :total="getOrderEntryReturnRequestTotalPrice(orderEntry, returnRequest)"
                    :trackingUrl="getOrderEntryReturnRequestEntry(orderEntry, returnRequest)?.orderEntry?.trackingUrl"
                  >
                    <template v-slot:image>
                      <SloProductImage
                        :source="getOrderEntryReturnRequestEntry(orderEntry, returnRequest)?.orderEntry?.product?.images | getProductThumbnail"
                      />
                    </template>
                  </MarketplaceRow>
                </div>
              </div>
            </template>
            <template v-slot:warnings>
              <div
                v-if="!orderEntry?.product?.isReturnable"
                class="flex items-center gap-sm mb-0 text-base fw-strong"
              >
                <span class="flex items-center justify-center c-orange-500">
                  <SloIcon name="TriangleExclamation" />
                </span>
                {{ 'This product cannot be returned once purchased' | t }}
              </div>
              <div
                v-if="!orderEntry?.product?.isCancellable"
                class="flex items-center gap-sm mb-0 text-base fw-strong"
              >
                <span class="flex items-center justify-center c-orange-500">
                  <SloIcon name="TriangleExclamation" />
                </span>
                {{ 'This product cannot be cancelled from your order once purchased' | t }}
              </div>
            </template>
          </MarketplaceRow>
          <div v-if="!isCancelled" class="flex items-center justify-between flex-wrap gap-md">
            <p v-if="entry?.orderEntries[0]?.shipDate" class="flex items-baseline gap-sm m-0 text-base fw-strong">
              <span class="c-green-400">
                <SloIcon name="Check" />
              </span>
              {{ getShipDateLabel(entry?.orderEntries[0]) }}: {{ entry?.orderEntries[0]?.shipDate }}
            </p>
            <p v-else-if="entry?.shippingDate" class="flex items-baseline gap-sm m-0 text-base fw-strong">
              <span class="c-green-400">
                <SloIcon name="Timer" />
              </span>
              {{ 'Estimated Shipping Date' | t }}: {{ entry?.shippingDate }}
            </p>
            <div class="flex items-center gap-md">
              <SloButton
                v-if="entry?.orderEntries[0]?.trackingUrl"
                :href="entry?.orderEntries[0]?.trackingUrl"
                fill="clear"
                target="_blank"
                class="bg-transparent p-0"
              >
                {{ 'Track Shipment' | t }}
              </SloButton>
              <SloButton
                v-if="entry?.orderEntries[0]?.docType"
                @click="handleInvoiceDownload(entry?.orderEntries[0]?.docType)"
                fill="clear"
                class="bg-transparent p-0"
              >
                {{ 'View Invoice' | t }}
              </SloButton>
            </div>
          </div>
        </div>
      </template>
      <template v-if="isCancelled" v-slot:customer-return>
        <div class="customer-return--reason">
          <p class="flex items-start gap-sm m-0">
            <span class="text-base fw-strong">{{ 'Reason' | t }}</span>
            {{ order?.cancellationReason }}
          </p>
        </div>
      </template>
      <template v-slot:footer>
        <MarketplaceTableFooter>
          <div class="marketplace-table--order-details-footer">
            <p class="m-0 text-sm fw-strong">
              {{ 'Purchased from Sloan Marketplace' | t }}
            </p>
            <div class="w-100">
              <div class="order-details--subtotal">
                <p>{{ 'Subtotal' | t }}</p>
                <p>{{ order?.subTotal?.formattedValue }}</p>
                <p v-if="order?.appliedCoupon">
                  {{ 'Coupon' | t }} ({{ order?.appliedCoupon }})
                </p>
                <p v-if="order?.appliedCoupon">
                  {{ order?.orderDiscounts?.formattedValue !== '$0.00' ? `-${order?.orderDiscounts?.formattedValue}` : '––' }}
                </p>
                <p>{{ 'Shipping' | t }}</p>
                <p>
                  {{ order?.shippingCost?.formattedValue == '$0.00' ? 'FREE' : order?.shippingCost?.formattedValue | t }}
                </p>
                <p>{{ 'Tax' | t }}</p>
                <p>{{ order?.tax?.formattedValue }}</p>
              </div>
              <hr>
              <div class="order-details--grand-total">
                <p class="fw-bold">{{ 'Total' | t }}</p>
                <p class="fw-bold">{{ order?.total?.formattedValue }}</p>
              </div>
            </div>
          </div>
        </MarketplaceTableFooter>
      </template>
    </MarketplaceTable>
    <div v-if="!authenticated" class="order-details--footer py-xl md:py-2xl">
      <p>
        {{ 'Need Help? Contact us' | t }}
        <a href="mailto:customer.service@sloanmarketplace.com">customer.service@sloanmarketplace.com</a>
        {{ 'or call us toll free' | t }}
        <a href="tel:+18666570023">866.657.0023</a>
      </p>
      <div class="flex items-end justify-end mt-xl md:mt-2xl">
        <SloButton :to="{name: 'marketplace-cart'}">
          {{ 'Continue Shopping' | t }}
        </SloButton>
      </div>
    </div>
    <ReferencesFullCarousel
      v-if="!authenticated && orderReferences && orderReferences.length > 0"
      :loading="orderReferencesLoading"
      :references="orderReferences"
    />
  </div>
</template>

<script>
import t from '../../main/i18n';
import {mapGetters} from 'vuex';
import {marketplaceApi} from '../apis/marketplace';
import {
  getProductThumbnail,
  getCardNumberLastFourDigits,
  getCardTypeIconName,
  calculateAndFormatPrice,
  formatOrderNumber,
  handleApiErrors
} from '../../main/utils';
import MarketplaceTable from '../components/MarketplaceTable.vue';
import MarketplaceTableHeader from '../components/MarketplaceTableHeader.vue';
import MarketplaceRow from '../components/MarketplaceRow.vue';
import MarketplaceTableFooter from '../components/MarketplaceTableFooter.vue';
import ReorderOrderButton from './ReorderOrderButton.vue';
import ReferencesFullCarousel from './ReferencesFullCarousel.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloCard from '../../../lib/components/SloCard.vue';
import SloAddToCart from '../../../lib/components/SloAddToCart.vue';
import SloProductMeta from '../../../lib/components/SloProductMeta.vue';
import SloLink from '../../../lib/components/SloLink.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';

export default {
  name: 'OrderDetails',
  components: {
    SloSelect,
    MarketplaceTable,
    MarketplaceTableHeader,
    MarketplaceRow,
    MarketplaceTableFooter,
    ReorderOrderButton,
    ReferencesFullCarousel,
    SloProductImage,
    SloField,
    SloLabel,
    SloModal,
    SloButton,
    SloCard,
    SloAddToCart,
    SloProductMeta,
    SloLink,
    SloIcon,
    SloLoader
  },
  filters: {
    t,
    getProductThumbnail,
    getCardNumberLastFourDigits,
    getCardTypeIconName,
    formatOrderNumber
  },
  data() {
    return {
      cancelOrderModal: false,
      cancelOrderLoading: false,
      cancelStatuses: [],
      cancelReason: null,
      orderReferencesLoading: false,
      orderReferences: []
    };
  },
  props: {
    order: {type: Object, required: true}
  },
  computed: {
    ...mapGetters('auth', ['access_token', 'authenticated']),
    isCancelled() {
      return this.order?.orderStatus === 'S004';
    },
    canCancel() {
      const orderStatus = this.order?.orderStatus;
      const someOrderProductsAreNotCancellable = this.order?.entries.some(entry =>
        entry?.orderEntries.some(orderEntry => !orderEntry?.product?.isCancellable)
      );

      return ['S001', 'S002', 'S010'].includes(orderStatus) && !someOrderProductsAreNotCancellable;
    },
    canReturn() {
      const orderStatus = this.order?.orderStatus;
      const allOrderProductsAreReturnable = this.order?.entries.some(entry =>
        entry?.orderEntries.some(orderEntry => orderEntry?.product?.isReturnable)
      );

      return ['S003', 'S009'].includes(orderStatus) && allOrderProductsAreReturnable;
    }
  },
  async created() {
    if (!this.authenticated) {
      this.orderReferencesLoading = true;
      const skus = this.order.entries.map(entry =>
        entry.orderEntries.map(orderEntry => orderEntry?.product?.code)
      ).flat();

      await marketplaceApi.getProductsReferences(skus)
        .then(res => {
          this.orderReferencesLoading = false;
          this.orderReferences = res.data.references;
        })
        .catch(() => this.orderReferencesLoading = false);
    }
  },
  methods: {
    setStatus(status) {
      this.status = status.toLowerCase();
    },
    async handleCancelOrder() {
      this.cancelOrderLoading = true;
      await marketplaceApi.cancelOrder({
        orderNumber: this.order?.orderNumber,
        email: this.order?.createdBy,
        billZip: this.order?.paymentInfo?.billingAddress?.postalCode,
        reason: this.cancelReason
      })
        .then(async() => {
          this.cancelOrderModal = false;
          this.cancelOrderLoading = false;
          this.$snotify.success(t('Order cancelled successfully'));
          await this.$router.go(0);
        })
        .catch(e => {
          this.cancelOrderLoading = false;
          handleApiErrors(e, this, 'Error cancelling order');
        });
    },
    getOrderEntryReturnRequestEntry(orderEntry, returnRequest) {
      return returnRequest?.returnEntries?.find(returnEntry =>
        returnEntry?.orderEntry?.entryNumber === orderEntry?.entryNumber
      );
    },
    getOrderEntryReturnRequestTotalPrice(orderEntry, returnRequest) {
      const returnRequestEntry = this.getOrderEntryReturnRequestEntry(orderEntry, returnRequest);
      const expectedQuantity = returnRequestEntry?.expectedQuantity;
      const price = returnRequestEntry?.orderEntry?.product?.price?.value;

      return calculateAndFormatPrice(price, expectedQuantity);
    },
    getProductPrice(orderEntry) {
      return orderEntry?.product?.subscriptionNumber
        ? orderEntry?.product?.subscriptionPrice?.formattedValue
        : orderEntry?.basePrice?.formattedValue;
    },
    getShipDateLabel(orderEntry) {
      return orderEntry?.trackingNumber || orderEntry?.docType
        ? t('Shipped On')
        : t('Estimated Ship Date');
    },
    getReturnRequestRouterObject(returnRequest) {
      if (this.authenticated) {
        return {
          name: 'marketplace-account-return',
          params: {code: returnRequest?.rma}
        };
      }

      return {
        name: 'marketplace-guest-return',
        query: {
          returncode: returnRequest?.rma,
          email: this.order?.createdBy,
          billZip: this.order?.paymentInfo?.billingAddress?.postalCode
        }
      };
    },
    async openCancelOrderModal() {
      this.cancelOrderModal = true;
      await marketplaceApi.getCancelStatuses()
        .then(res => this.cancelStatuses = res.data)
        .catch(e => handleApiErrors(e, this, 'Error retrieving cancel statuses'));
    },
    handleCancelCancelOrder() {
      this.cancelOrderModal = false;
      this.cancelReason = null;
    },
    handlePrintOrder() {
      window.print();
    },
    handleInvoiceDownload(docType) {
      fetch(
        `${window.location.origin}/marketplace/api` +
        `/users/${this.$store.getters['marketplace/userId']}` +
        `/getPdf?documentNumber=${docType}`,
        this.access_token ? {headers: {Authorization: `Bearer ${this.access_token}`}} : {}
      )
        .then(resp => resp.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `${docType}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => this.$snotify.error(t('Error downloading invoice')));
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/variables';
@import '../../../scss/system';

.order-details {
  h1, h2 {
    font-weight: normal;
    font-size: 3.6rem;
    margin: 0;

    @media (min-width: $screen-sm) {
      @include text('3xl');
    }
  }
}

.marketplace-order--return {
  border-radius: 0.8rem;
  padding: 1.6rem;

  @media print {
    padding-block: 0 !important;
  }
}

.marketplace-table--order-details-footer {
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
  gap: 1.6rem;
  width: 100%;

  @media (min-width: $screen-sm-min) {
    flex-direction: row;
  }

  > * {
    @media (min-width: $screen-sm-min) {
      flex: 1 1 50%;
    }
  }

  .order-details {
    &--subtotal, &--grand-total {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.6rem;

      p {
        margin: 0;

        &:nth-of-type(even) {
          place-self: end;
        }
      }
    }
  }
}

.fade-up-enter, .fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.fade-up-enter-active, .fade-up-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.order-details-related-products {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3.2rem;
}

.customer-return {
  &--reason {
    padding: 1.6rem;
    background-color: #fffbeb;
    border-top: 1px solid $color-gray-400;
  }
}

.order-details--footer {
  @media print {
    display: none;
  }
}
</style>
